
import React from "react";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

type Gender = "Male" | "Female";

export const genders: Gender[] = ["Male", "Female"];

export function PersonalInfoSection({ nicknameError, checkingNickname }: { nicknameError: string | null, checkingNickname: boolean }) {
  return (
    <div className="bg-background rounded-xl md:rounded-2xl border shadow-md p-6 md:p-8 mb-8 transition duration-300">
      <h3 className="text-xl font-semibold mb-6" style={{ color: "hsl(var(--primary))" }}>
        Personal Information
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* First Name */}
        <FormField
          name="firstName"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-1">
                First Name <span className="text-destructive">*</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="ml-1 flex items-center"><Info size={16} /></span>
                  </TooltipTrigger>
                  <TooltipContent>Your legal first name.</TooltipContent>
                </Tooltip>
              </FormLabel>
              <FormControl>
                <Input {...field} autoComplete="given-name" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Last Name */}
        <FormField
          name="lastName"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-1">
                Last Name <span className="text-destructive">*</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="ml-1 flex items-center"><Info size={16} /></span>
                  </TooltipTrigger>
                  <TooltipContent>Your legal last name.</TooltipContent>
                </Tooltip>
              </FormLabel>
              <FormControl>
                <Input {...field} autoComplete="family-name" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-5">
        {/* Nickname */}
        <FormField
          name="nickname"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-1">
                Nickname <span className="text-destructive">*</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="ml-1 flex items-center"><Info size={16} /></span>
                  </TooltipTrigger>
                  <TooltipContent>
                    This will be used in chats for non-business community members.
                  </TooltipContent>
                </Tooltip>
              </FormLabel>
              <FormControl>
                <Input {...field} autoComplete="off" />
              </FormControl>
              <FormMessage />
              {nicknameError && <div className="text-xs text-destructive mt-1">{nicknameError}</div>}
              {checkingNickname && <div className="text-xs text-muted-foreground mt-1">Checking nickname...</div>}
            </FormItem>
          )}
        />

        {/* Gender */}
        <FormField
          name="gender"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-1">
                Gender <span className="text-destructive">*</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="ml-1 flex items-center"><Info size={16} /></span>
                  </TooltipTrigger>
                  <TooltipContent>
                    We require your gender for demographic analytics.
                  </TooltipContent>
                </Tooltip>
              </FormLabel>
              <FormControl>
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select gender..." />
                  </SelectTrigger>
                  <SelectContent>
                    {genders.map(g => (
                      <SelectItem key={g} value={g}>{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  )
}
